import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Factory, ProductionTab } from '../../production-inventory.model';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { finalize, forkJoin } from 'rxjs';
import { FormControl } from '@angular/forms';
import { AbstractTabComponent } from '../../../common/abstracts/abstract-tab.component';
import { ActivatedRoute, Router } from '@angular/router';
import { TabItem } from '../../../common/models/common.model';
import { CompanyFactoryService } from '../../services/company-factory.service';

@Component({
  selector: 'app-production-overview',
  standalone: false,
  templateUrl: './production-overview.component.html',
  styleUrl: './production-overview.component.scss',
})
export class ProductionOverviewComponent extends AbstractTabComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabNav') tabNav!: ElementRef<HTMLElement>;

  public override selectedTab: string = ProductionTab.PRODUCTION_ENTRIES;
  protected rawMaterialWarehouse: RawMaterialWarehouse[] = [];
  protected factories: Factory[] = [];
  protected isInitialDataLoading: boolean = false;
  // null (not '') so ng-select shows the "All Warehouses" placeholder instead of a blank box
  protected warehouseControl = new FormControl<string | null>(null);
  protected canScrollLeft = false;
  protected canScrollRight = false;

  public override tabs: TabItem[] = [
    {
      label: 'Production Entries',
      value: ProductionTab.PRODUCTION_ENTRIES,
      icon: 'bi bi-journal-plus',
      permissionCode: 'ui_production.entries.view',
    },
    {
      label: 'Factory Stock',
      value: ProductionTab.FACTORY_STOCK,
      icon: 'bi bi-boxes',
      permissionCode: 'ui_production.factory_stock.view',
    },
    {
      label: 'Transfers',
      value: ProductionTab.PRODUCT_TRANSFERS,
      icon: 'bi bi-arrow-left-right',
      permissionCode: 'ui_production.transfers.view',
    },
    {
      label: 'Labours',
      value: ProductionTab.LABOURS,
      icon: 'bi bi-people',
      permissionCode: 'ui_production.labours.view',
    },
    {
      label: 'Daily Attendance',
      value: ProductionTab.LABOURS_DAILY_ATTENDANCE,
      icon: 'bi bi-calendar-check',
      permissionCode: 'ui_production.daily_attendance.view',
    },
    {
      label: 'Factory Adjustments',
      value: ProductionTab.FACTORY_STOCK_ADJUSTMENTS,
      icon: 'bi bi-sliders',
      permissionCode: 'ui_production.factory_adjustments.view',
    },
    {
      label: 'Warehouse Adjustments',
      value: ProductionTab.WAREHOUSE_STOCK_ADJUSTMENTS,
      icon: 'bi bi-sliders',
      permissionCode: 'ui_production.warehouse_adjustments.view',
    },
  ];

  constructor(
    private rawMaterialService: RawMaterialService,
    private companyFactoryService: CompanyFactoryService,
    protected override route: ActivatedRoute,
    protected override router: Router
  ) {
    super(route, router);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateScrollButtons());
  }

  protected onTabScroll(): void {
    this.updateScrollButtons();
  }

  protected scrollTabs(dir: -1 | 1): void {
    this.tabNav.nativeElement.scrollBy({ left: dir * 200, behavior: 'smooth' });
  }

  private updateScrollButtons(): void {
    const el = this.tabNav.nativeElement;
    this.canScrollLeft = el.scrollLeft > 0;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  }

  override ngOnInit(): void {
    // filterTabs() in the base class drops the tabs this designation cannot open, and
    // isValidTab then rejects a ?tab= deep link pointing at one of them.
    super.ngOnInit();
    this.getInitialData();
  }

  public override selectTab(tab: string): void {
    super.selectTab(tab);
  }

  private getInitialData(): void {
    this.isInitialDataLoading = true;

    const rawMaterialWarehouses$ = this.rawMaterialService.get_warehouses();
    const factories$ = this.companyFactoryService.listFactories();

    forkJoin([rawMaterialWarehouses$, factories$])
      .pipe(finalize(() => (this.isInitialDataLoading = false)))
      .subscribe({
        next: ([rawMaterialWarehouses, factories]: [RawMaterialWarehouse[], Factory[]]) => {
          this.rawMaterialWarehouse = rawMaterialWarehouses;
          this.factories = factories;
        },
        error: err => {
          // Handle error
        },
      });
  }

  protected isAdminOnlyTab(tab: TabItem): boolean {
    return tab.roles?.includes('admin') ?? false;
  }

  ngOnDestroy(): void {}

  protected readonly ProductionTab = ProductionTab;
}
